"use client";

import { ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from "@apollo/experimental-nextjs-app-support";
import { createUploadLink } from "apollo-upload-client";
import { useCallback } from "react";
import { useConfig } from "@/context/ConfigContext";
import { createErrorLink } from "./authLink";
import { withPersistedQueries } from "./persistedQueries";
import { roomAccessTokenLink } from "./roomAccessTokenLink";

const defaultHeaders = setContext((_) => ({
  headers: {
    "X-Application-Id": "guest-connect-frontend",
    "apollo-require-preflight": "true",
  },
}));

const createMakeClient = (guestHubGraphqlUrl: string) => () => {
  const errorLink = createErrorLink(guestHubGraphqlUrl);

  const uploadLink = createUploadLink({
    uri: guestHubGraphqlUrl,
    fetchOptions: {
      cache: "no-store",
      credentials: "include",
    },
  });

  const terminatingLink =
    typeof window === "undefined"
      ? new SSRMultipartLink({ stripDefer: true }).concat(
          uploadLink as unknown as ApolloLink,
        )
      : (uploadLink as unknown as ApolloLink);

  const link = ApolloLink.from([
    defaultHeaders,
    roomAccessTokenLink,
    errorLink,
    withPersistedQueries(terminatingLink),
  ]);

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Booking: {
          keyFields: ["id"],
          fields: {
            pricing: { merge: true },
          },
        },
        BookingGuestStay: {
          keyFields: ["guestId"],
        },
      },
    }),
    link,
  });
};

export const ApolloWrapper = ({ children }: React.PropsWithChildren) => {
  const { config, loading } = useConfig();

  const makeClient = useCallback(
    () => createMakeClient(config?.guestHubGraphqlUrl ?? "")(),
    [config?.guestHubGraphqlUrl],
  );

  if (loading || !config) {
    return null;
  }

  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
};
